import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/Exchange.css';
import { exchangeService, authService } from '../services/api';

const numberFormatter = new Intl.NumberFormat('es-DO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const currencyFormatter = (monto, currency = 'DOP') =>
  new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(monto || 0);

const getTodayISO = () => new Date().toISOString().split('T')[0];
const getFirstDayMonthISO = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split('T')[0];
};

const Exchange = () => {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const tenantInfo = localStorage.getItem('tenant_info') ? JSON.parse(localStorage.getItem('tenant_info')) : null;
  const planNombre = (tenantInfo?.plan || '').toString().toLowerCase();
  const esPremium = planNombre.includes('premium');

  const [loading, setLoading] = useState(true);
  const [permitido, setPermitido] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [monedas, setMonedas] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [operaciones, setOperaciones] = useState([]);
  const [tasasEditadas, setTasasEditadas] = useState({});
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [posTipo, setPosTipo] = useState('venta');
  const [form, setForm] = useState({
    moneda: 'USD',
    cantidad: '',
    tasa: '',
    cliente: '',
    autorizacion: '',
    notas: ''
  });
  const [enviando, setEnviando] = useState(false);
  const [actualizandoTasa, setActualizandoTasa] = useState('');
  const user = authService.getCurrentUser();
  const esAdminExchange = (user?.rol || '').toLowerCase().includes('admin');
  const [flujoFecha, setFlujoFecha] = useState(getTodayISO());
  const [flujoDiario, setFlujoDiario] = useState(null);
  const [gananciasFiltro, setGananciasFiltro] = useState({
    desde: getFirstDayMonthISO(),
    hasta: getTodayISO(),
    moneda: ''
  });
  const [gananciasDatos, setGananciasDatos] = useState(null);
  const [operacionesHistorico, setOperacionesHistorico] = useState([]);
  const [cargandoReporte, setCargandoReporte] = useState(false);
  const [operacionesFiltro, setOperacionesFiltro] = useState({
    moneda: '',
    tipo: '',
    limite: 30
  });
  const [configExchange, setConfigExchange] = useState(null);
  const [capitalForm, setCapitalForm] = useState({
    capital_inicial: '',
    capital_disponible: '',
    capital_reservado: ''
  });
  const [guardandoCapital, setGuardandoCapital] = useState(false);
  const [inyeccionForm, setInyeccionForm] = useState({
    moneda: '',
    cantidad: '',
    tasa: '',
    proveedor: '',
    descripcion: '',
    autorizacion: ''
  });
  const [inyecciones, setInyecciones] = useState([]);
  const [inyectando, setInyectando] = useState(false);
  const montoInyeccion = useMemo(() => {
    const cantidad = parseFloat(inyeccionForm.cantidad) || 0;
    const tasa = parseFloat(inyeccionForm.tasa) || 0;
    return cantidad * tasa;
  }, [inyeccionForm.cantidad, inyeccionForm.tasa]);
  const puedeRegistrarInyeccion = useMemo(() => {
    const cantidad = parseFloat(inyeccionForm.cantidad);
    const tasa = parseFloat(inyeccionForm.tasa);
    return Boolean(inyeccionForm.moneda && cantidad > 0 && tasa > 0);
  }, [inyeccionForm]);

  useEffect(() => {
    setPermitido(esPremium);
    if (!esPremium) {
      setLoading(false);
      return;
    }
    cargarDatos();
    cargarOperaciones();
    cargarReporteFlujo(getTodayISO());
    cargarReporteGanancias(gananciasFiltro);
  }, [esPremium]);

  useEffect(() => {
    if (esAdminExchange) {
      cargarInyecciones();
    }
  }, [esAdminExchange]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [dashboardRes, monedasRes, inventarioRes, configRes] = await Promise.all([
        exchangeService.getDashboard(),
        exchangeService.getMonedas(),
        exchangeService.getInventario(),
        exchangeService.getConfig()
      ]);

      setDashboard(dashboardRes.data);
      setMonedas(monedasRes.data.monedas || []);
      setInventario(inventarioRes.data.inventario || []);
      setConfigExchange(configRes.data.config);
      setCapitalForm({
        capital_inicial: configRes.data.config.capital_inicial?.toString() || '',
        capital_disponible: configRes.data.config.capital_disponible?.toString() || '',
        capital_reservado: configRes.data.config.capital_reservado?.toString() || ''
      });

      const tasasIniciales = {};
      (monedasRes.data.monedas || []).forEach((moneda) => {
        tasasIniciales[moneda.codigo] = {
          compra: moneda.tasa_compra?.toString() || '',
          venta: moneda.tasa_venta?.toString() || '',
          activo: !!moneda.activo
        };
      });
      setTasasEditadas(tasasIniciales);

      if (!form.moneda && monedasRes.data.monedas?.length) {
        const primera = monedasRes.data.monedas[0];
        setForm((prev) => ({
          ...prev,
          moneda: primera.codigo,
          tasa: (posTipo === 'compra' ? primera.tasa_compra : primera.tasa_venta)?.toString() || ''
        }));
      }

      if (!inyeccionForm.moneda && monedasRes.data.monedas?.length) {
        const primera = monedasRes.data.monedas[0];
        setInyeccionForm((prev) => ({
          ...prev,
          moneda: primera.codigo,
          tasa: primera.tasa_compra?.toString() || prev.tasa
        }));
      }

      setMensaje({ tipo: '', texto: '' });
    } catch (error) {
      console.error('Error cargando datos de exchange', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo cargar la información del módulo de cambio.'
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarOperaciones = async (filtros = operacionesFiltro) => {
    try {
      const params = {
        limit: filtros.limite,
        ...(filtros.moneda ? { moneda: filtros.moneda } : {}),
        ...(filtros.tipo ? { tipo: filtros.tipo } : {})
      };
      const response = await exchangeService.listOperaciones(params);
      const lista = response.data.operaciones || [];
      setOperaciones(lista);
      setOperacionesHistorico(lista.slice(0, 10).reverse());
    } catch (error) {
      console.error('Error cargando operaciones', error);
    }
  };

  const cargarInyecciones = async () => {
    if (!esAdminExchange) return;
    try {
      const { data } = await exchangeService.getInyecciones();
      setInyecciones(data.inyecciones || []);
    } catch (error) {
      console.error('Error cargando inyecciones', error);
    }
  };

  const monedaSeleccionada = useMemo(() => monedas.find((m) => m.codigo === form.moneda), [monedas, form.moneda]);

  const graficoGanancias = useMemo(() => {
    if (!gananciasDatos?.registros?.length) return null;
    const etiquetas = [...gananciasDatos.registros].reverse().map((item) => item.fecha);
    const valores = [...gananciasDatos.registros].reverse().map((item) => item.ganancia || 0);
    return {
      labels: etiquetas,
      datasets: [
        {
          label: 'Ganancia (DOP)',
          data: valores,
          borderColor: '#0d47a1',
          backgroundColor: 'rgba(13, 71, 161, 0.1)',
          tension: 0.3
        }
      ]
    };
  }, [gananciasDatos]);

  useEffect(() => {
    if (!monedaSeleccionada) return;
    const tasaPorDefecto = posTipo === 'compra' ? monedaSeleccionada.tasa_compra : monedaSeleccionada.tasa_venta;
    setForm((prev) => ({
      ...prev,
      tasa: tasaPorDefecto?.toString() || ''
    }));
  }, [posTipo, monedaSeleccionada]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const totalPesos = useMemo(() => {
    const cantidad = parseFloat(form.cantidad) || 0;
    const tasa = parseFloat(form.tasa) || 0;
    return cantidad * tasa;
  }, [form.cantidad, form.tasa]);

  const handleRegistroOperacion = async (e) => {
    e.preventDefault();
    if (enviando) return;
    const cantidad = parseFloat(form.cantidad);
    const tasa = parseFloat(form.tasa);

    if (!form.moneda || !posTipo) {
      setMensaje({ tipo: 'error', texto: 'Debe seleccionar la moneda y el tipo de operación.' });
      return;
    }

    if (!cantidad || cantidad <= 0 || !tasa || tasa <= 0) {
      setMensaje({ tipo: 'error', texto: 'La cantidad y la tasa deben ser mayores a cero.' });
      return;
    }

    try {
      setEnviando(true);
      await exchangeService.createOperacion({
        tipo: posTipo,
        moneda: form.moneda,
        cantidad: cantidad,
        tasa: tasa,
        cliente: form.cliente || undefined,
        autorizacion: form.autorizacion || undefined,
        notas: form.notas || undefined
      });
      setMensaje({ tipo: 'success', texto: 'Operación registrada correctamente.' });
      setForm((prev) => ({
        ...prev,
        cantidad: '',
        notas: '',
        autorizacion: '',
        cliente: ''
      }));
      cargarDatos();
    } catch (error) {
      console.error('Error creando operación', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo registrar la operación.'
      });
    } finally {
      setEnviando(false);
    }
  };

  const handleCambioTasa = (codigo, campo, valor) => {
    setTasasEditadas((prev) => ({
      ...prev,
      [codigo]: {
        ...prev[codigo],
        [campo]: valor
      }
    }));
  };

  const handleActualizarTasa = async (codigo) => {
    if (!tasasEditadas[codigo]) return;
    const compra = parseFloat(tasasEditadas[codigo].compra);
    const venta = parseFloat(tasasEditadas[codigo].venta);
    if (!compra || !venta) {
      setMensaje({ tipo: 'error', texto: 'Las tasas deben ser números válidos.' });
      return;
    }
    try {
      setActualizandoTasa(codigo);
      await exchangeService.updateMoneda(codigo, {
        tasa_compra: compra,
        tasa_venta: venta,
        activo: tasasEditadas[codigo].activo
      });
      setMensaje({ tipo: 'success', texto: `Tasas actualizadas para ${codigo}.` });
      cargarDatos();
    } catch (error) {
      console.error('Error actualizando tasa', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo actualizar la tasa.'
      });
    } finally {
      setActualizandoTasa('');
    }
  };

  const cargarReporteFlujo = async (fecha) => {
    try {
      setCargandoReporte(true);
      const { data } = await exchangeService.getFlujoDiario({ fecha });
      setFlujoDiario(data);
      setMensaje((prev) => (prev.tipo === 'success' ? prev : { tipo: '', texto: '' }));
    } catch (error) {
      console.error('Error cargando flujo diario', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo obtener el flujo diario.'
      });
    } finally {
      setCargandoReporte(false);
    }
  };

  const cargarReporteGanancias = async (filtros) => {
    try {
      setCargandoReporte(true);
      const { data } = await exchangeService.getGanancias(filtros);
      setGananciasDatos(data);
    } catch (error) {
      console.error('Error cargando ganancias', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo obtener el reporte de ganancias.'
      });
    } finally {
      setCargandoReporte(false);
    }
  };

  const handleTicket = async (operacionId) => {
    try {
      const { data } = await exchangeService.getTicketOperacion(operacionId);
      const nuevaVentana = window.open('', '_blank', 'width=380,height=600');
      if (!nuevaVentana) {
        setMensaje({ tipo: 'error', texto: 'El navegador bloqueó la ventana de impresión.' });
        return;
      }
      const oper = data.operacion;
      nuevaVentana.document.write(`
        <html>
          <head>
            <title>Ticket Exchange</title>
            <style>
              body { font-family: 'Courier New', monospace; padding: 10px; }
              h2 { text-align: center; margin: 0 0 10px; }
              .row { display: flex; justify-content: space-between; margin-bottom: 4px; }
              .divider { border-top: 1px dashed #888; margin: 10px 0; }
            </style>
          </head>
          <body>
            <h2>N3O Exchange</h2>
            <div class="row"><span>Operación:</span><strong>${oper.tipo.toUpperCase()} ${oper.moneda}</strong></div>
            <div class="divider"></div>
            <div class="row"><span>Cantidad:</span><strong>${numberFormatter.format(oper.cantidad)}</strong></div>
            <div class="row"><span>Tasa:</span><strong>${numberFormatter.format(oper.tasa)}</strong></div>
            <div class="row"><span>Total pesos:</span><strong>${currencyFormatter(oper.monto_pesos)}</strong></div>
            <div class="row"><span>Ganancia:</span><strong>${currencyFormatter(oper.ganancia)}</strong></div>
            ${oper.cliente ? `<div class="row"><span>Cliente:</span><strong>${oper.cliente}</strong></div>` : ''}
            ${oper.autorizacion ? `<div class="row"><span>Autorización:</span><strong>${oper.autorizacion}</strong></div>` : ''}
            <div class="divider"></div>
            <div class="row"><span>Fecha:</span><strong>${new Date(oper.fecha).toLocaleString()}</strong></div>
            <div class="row"><span>Ticket:</span><strong>#${oper.id}</strong></div>
          </body>
        </html>
      `);
      nuevaVentana.document.close();
      nuevaVentana.focus();
      nuevaVentana.print();
    } catch (error) {
      console.error('Error generando ticket', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo generar el ticket.'
      });
    }
  };

  const descargarArchivo = (blob, nombre) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nombre);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportarFlujo = async (formato) => {
    try {
      const params = { fecha: flujoFecha, formato };
      const response = await exchangeService.exportFlujoDiario(params);
      const nombre = formato === 'excel'
        ? `flujo-diario-${flujoFecha}.csv`
        : `flujo-diario-${flujoFecha}.pdf`;
      descargarArchivo(response.data, nombre);
    } catch (error) {
      console.error('Error exportando flujo', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo exportar el flujo diario.'
      });
    }
  };

  const generarTextoTicket = (ticket) => {
    const oper = ticket.operacion;
    const lineas = [
      'N3O Exchange',
      `Operación: ${oper.tipo?.toUpperCase()} ${oper.moneda}`,
      `Cantidad: ${numberFormatter.format(oper.cantidad)} ${oper.moneda}`,
      `Tasa: ${numberFormatter.format(oper.tasa)}`,
      `Total DOP: ${currencyFormatter(oper.monto_pesos)}`,
      `Ganancia: ${currencyFormatter(oper.ganancia)}`,
      oper.cliente ? `Cliente: ${oper.cliente}` : null,
      oper.autorizacion ? `Autorización: ${oper.autorizacion}` : null,
      `Ticket: #${oper.id}`,
      `Fecha: ${oper.fecha ? new Date(oper.fecha).toLocaleString() : new Date().toLocaleString()}`
    ].filter(Boolean);
    return lineas.join('\n');
  };

  const enviarWhatsApp = async (operacionId) => {
    try {
      const numero = prompt('Número de WhatsApp (solo números, incluido código país):');
      if (!numero) return;
      const { data } = await exchangeService.getTicketOperacion(operacionId);
      const texto = generarTextoTicket(data);
      const url = `https://wa.me/${numero.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(texto)}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error preparando mensaje de WhatsApp', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo preparar el ticket para WhatsApp.'
      });
    }
  };

  const enviarCorreo = async (operacionId) => {
    try {
      const correo = prompt('Correo del cliente:');
      if (!correo) return;
      const { data } = await exchangeService.getTicketOperacion(operacionId);
      const texto = generarTextoTicket(data);
      const asunto = `Ticket Exchange #${data.operacion.id}`;
      const url = `mailto:${correo}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(texto)}`;
      window.location.href = url;
    } catch (error) {
      console.error('Error preparando correo', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo preparar el ticket para correo.'
      });
    }
  };

  const exportarGanancias = async (formato) => {
    try {
      const params = {
        desde: gananciasFiltro.desde,
        hasta: gananciasFiltro.hasta,
        moneda: gananciasFiltro.moneda || undefined,
        formato
      };
      const response = await exchangeService.exportGanancias(params);
      const nombre = formato === 'excel'
        ? `ganancias-${gananciasFiltro.desde}-${gananciasFiltro.hasta}.csv`
        : `ganancias-${gananciasFiltro.desde}-${gananciasFiltro.hasta}.pdf`;
      descargarArchivo(response.data, nombre);
    } catch (error) {
      console.error('Error exportando ganancias', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo exportar el reporte de ganancias.'
      });
    }
  };

  const [alertas, setAlertas] = useState({ inventario_bajo: [], ganancia_hoy: 0 });
  const [cerrando, setCerrando] = useState(false);

  const cargarAlertas = async () => {
    try {
      const { data } = await exchangeService.getAlertas();
      setAlertas(data);
    } catch (error) {
      console.error('Error cargando alertas', error);
    }
  };

  const handleCapitalChange = (field, value) => {
    setCapitalForm((prev) => {
      if (field === 'capital_inicial') {
        const shouldSyncDisponible = !prev.capital_disponible || prev.capital_disponible === '0';
        return {
          ...prev,
          capital_inicial: value,
          capital_disponible: shouldSyncDisponible ? value : prev.capital_disponible
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleInyeccionChange = (field, value) => {
    setInyeccionForm((prev) => {
      if (field === 'moneda') {
        const monedaSeleccionada = monedas.find((m) => m.codigo === value);
        return {
          ...prev,
          moneda: value,
          tasa: monedaSeleccionada ? (monedaSeleccionada.tasa_compra?.toString() || '') : prev.tasa
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const guardarCapital = async () => {
    try {
      setGuardandoCapital(true);
      const payload = { ...capitalForm };
      if ((!payload.capital_disponible || Number(payload.capital_disponible) === 0) && payload.capital_inicial) {
        payload.capital_disponible = payload.capital_inicial;
      }
      await exchangeService.updateConfig(payload);
      setMensaje({ tipo: 'success', texto: 'Capital actualizado correctamente.' });
      cargarDatos();
    } catch (error) {
      console.error('Error actualizando capital', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo actualizar el capital.'
      });
    } finally {
      setGuardandoCapital(false);
    }
  };

  const registrarInyeccion = async () => {
    if (!puedeRegistrarInyeccion) {
      setMensaje({
        tipo: 'error',
        texto: 'Completa la moneda, cantidad y tasa para registrar la compra.'
      });
      return;
    }

    try {
      setInyectando(true);
      await exchangeService.createInyeccion({
        moneda: inyeccionForm.moneda,
        cantidad: parseFloat(inyeccionForm.cantidad),
        tasa: parseFloat(inyeccionForm.tasa),
        proveedor: inyeccionForm.proveedor || undefined,
        descripcion: inyeccionForm.descripcion || undefined,
        autorizacion: inyeccionForm.autorizacion || undefined
      });
      setMensaje({ tipo: 'success', texto: 'Inventario alimentado correctamente.' });
      await cargarDatos();
      await cargarInyecciones();
      const monedaActual = monedas.find((m) => m.codigo === inyeccionForm.moneda);
      setInyeccionForm((prev) => ({
        ...prev,
        cantidad: '',
        tasa: monedaActual ? (monedaActual.tasa_compra?.toString() || '') : '',
        proveedor: '',
        descripcion: '',
        autorizacion: ''
      }));
    } catch (error) {
      console.error('Error registrando inyección', error);
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo registrar la inyección.'
      });
    } finally {
      setInyectando(false);
    }
  };

  const ejecutarCierre = async () => {
    if (!window.confirm('¿Registrar el cierre del día?')) return;
    try {
      setCerrando(true);
      await exchangeService.runCierre();
      setMensaje({ tipo: 'success', texto: 'Cierre registrado correctamente.' });
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: error.response?.data?.error || 'No se pudo registrar el cierre.'
      });
    } finally {
      setCerrando(false);
    }
  };

  useEffect(() => {
    cargarAlertas();
  }, []);

  if (loading) {
    return <div className="loading">Cargando módulo de cambio...</div>;
  }

  if (!permitido) {
    return (
      <div className="exchange-page">
        <div className="banner error">
          Este módulo solo está disponible para clientes con plan premium.
        </div>
        <button className="btn" onClick={() => navigate(`/${codigo || ''}/dashboard`)}>
          ← Volver al Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="exchange-page">
      <div className="exchange-header">
        <div>
          <h1>N3O Exchange · Tasa de Cambio</h1>
          <p>Gestiona tus compras y ventas de divisas con control total del inventario.</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={cargarDatos}>
            ⟳ Actualizar
          </button>
          {esAdminExchange && (
            <button className="btn btn-primary" onClick={ejecutarCierre} disabled={cerrando}>
              {cerrando ? 'Cerrando...' : 'Cerrar Día'}
            </button>
          )}
        </div>
      </div>

      {mensaje.texto && (
        <div className={`banner ${mensaje.tipo === 'error' ? 'error' : 'success'}`}>
          {mensaje.texto}
        </div>
      )}

      <section className="exchange-kpis">
        <div className="kpi-card">
          <span>Capital Disponible</span>
          <strong>{currencyFormatter(dashboard?.resumen?.capital_disponible || 0)}</strong>
        </div>
        <div className="kpi-card">
          <span>Inventario (valor compra)</span>
          <strong>{currencyFormatter(dashboard?.resumen?.inventario_valorado_compra || 0)}</strong>
        </div>
        <div className="kpi-card">
          <span>Inventario (valor venta)</span>
          <strong>{currencyFormatter(dashboard?.resumen?.inventario_valorado_venta || 0)}</strong>
        </div>
        <div className="kpi-card">
          <span>Ganancia de Hoy</span>
          <strong className={dashboard?.resumen?.ganancia_hoy >= 0 ? 'positivo' : 'negativo'}>
            {currencyFormatter(dashboard?.resumen?.ganancia_hoy || 0)}
          </strong>
        </div>
        <div className="kpi-card alerta-card">
          <span>Alerta Inventario</span>
          {alertas.inventario_bajo?.length ? (
            <>
              <strong className="negativo">{alertas.inventario_bajo.length} monedas</strong>
              <small>Sin disponibilidad</small>
            </>
          ) : (
            <strong className="positivo">Inventario OK</strong>
          )}
        </div>
      </section>
      {esAdminExchange && (
        <section className="capital-card">
          <header>
            <div>
              <h2>Capital y reservas</h2>
              <p>Actualiza el capital en pesos para registrar compras de divisa.</p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={guardarCapital} disabled={guardandoCapital}>
              {guardandoCapital ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </header>
          <div className="capital-grid">
            <div className="form-row">
              <label>Capital inicial</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={capitalForm.capital_inicial}
                onChange={(e) => handleCapitalChange('capital_inicial', e.target.value)}
              />
            </div>
            <div className="form-row">
              <label>Capital disponible</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={capitalForm.capital_disponible}
                onChange={(e) => handleCapitalChange('capital_disponible', e.target.value)}
              />
            </div>
          </div>
        </section>
      )}
      {esAdminExchange && (
        <section className="inyeccion-card">
          <header>
            <div>
              <h2>Inyección de inventario</h2>
              <p>Convierte parte del capital en divisas para abastecer al POS.</p>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={registrarInyeccion}
              disabled={inyectando || !puedeRegistrarInyeccion}
            >
              {inyectando ? 'Registrando...' : 'Registrar compra'}
            </button>
          </header>
          <div className="inyeccion-grid">
            <div className="form-row">
              <label>Moneda</label>
              <select value={inyeccionForm.moneda} onChange={(e) => handleInyeccionChange('moneda', e.target.value)}>
                <option value="">Selecciona</option>
                {monedas.map((moneda) => (
                  <option key={moneda.codigo} value={moneda.codigo}>
                    {moneda.codigo} · {moneda.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>Cantidad</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={inyeccionForm.cantidad}
                onChange={(e) => handleInyeccionChange('cantidad', e.target.value)}
              />
            </div>
            <div className="form-row">
              <label>Tasa de compra</label>
              <input
                type="number"
                min="0"
                step="0.0001"
                value={inyeccionForm.tasa}
                onChange={(e) => handleInyeccionChange('tasa', e.target.value)}
              />
            </div>
            <div className="form-row">
              <label>Proveedor (opcional)</label>
              <input
                type="text"
                value={inyeccionForm.proveedor}
                onChange={(e) => handleInyeccionChange('proveedor', e.target.value)}
              />
            </div>
            <div className="form-row">
              <label>Autorización (opcional)</label>
              <input
                type="text"
                value={inyeccionForm.autorizacion}
                onChange={(e) => handleInyeccionChange('autorizacion', e.target.value)}
              />
            </div>
            <div className="form-row form-row-textarea">
              <label>Notas</label>
              <textarea
                rows="2"
                value={inyeccionForm.descripcion}
                onChange={(e) => handleInyeccionChange('descripcion', e.target.value)}
              />
            </div>
          </div>
          <div className="inyeccion-summary">
            <div>
              <span>Total estimado en pesos</span>
              <strong>{currencyFormatter(montoInyeccion || 0)}</strong>
            </div>
            <small>El monto se descuenta automáticamente del capital disponible.</small>
          </div>
          <div className="inyeccion-historial">
            <h3>Últimas inyecciones</h3>
            {inyecciones.length === 0 ? (
              <p className="empty">Aún no has registrado inyecciones administrativas.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Moneda</th>
                    <th>Cantidad</th>
                    <th>Tasa</th>
                    <th>Total RD$</th>
                  </tr>
                </thead>
                <tbody>
                  {inyecciones.map((inyeccion) => (
                    <tr key={inyeccion.id}>
                      <td>{new Date(inyeccion.created_at).toLocaleString('es-DO')}</td>
                      <td>{inyeccion.moneda} · {inyeccion.moneda_nombre}</td>
                      <td>{numberFormatter.format(inyeccion.cantidad_divisa || 0)}</td>
                      <td>{numberFormatter.format(inyeccion.tasa || 0)}</td>
                      <td>{currencyFormatter(inyeccion.monto_pesos || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}
      {alertas.inventario_bajo?.length > 0 && (
        <div className="alertas-banner">
          <strong>⚠️ Monedas sin stock:</strong>
          <ul>
            {alertas.inventario_bajo.map((item) => (
              <li key={item.codigo}>
                {item.codigo}: {numberFormatter.format(item.cantidad_actual || 0)} disponible
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="exchange-grid">
        <section className="pos-card">
          <header>
            <h2>POS · Compra/Venta</h2>
            <div className="pos-toggle">
              <button
                className={posTipo === 'compra' ? 'active' : ''}
                onClick={() => setPosTipo('compra')}
              >
                Comprar
              </button>
              <button
                className={posTipo === 'venta' ? 'active' : ''}
                onClick={() => setPosTipo('venta')}
              >
                Vender
              </button>
            </div>
          </header>

          <form onSubmit={handleRegistroOperacion} className="pos-form">
            <div className="form-row">
              <label>Moneda</label>
              <select
                value={form.moneda}
                onChange={(e) => handleFormChange('moneda', e.target.value)}
              >
                {monedas.map((moneda) => (
                  <option key={moneda.id} value={moneda.codigo}>
                    {moneda.codigo} · {moneda.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>Cantidad ({form.moneda})</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.cantidad}
                onChange={(e) => handleFormChange('cantidad', e.target.value)}
                required
              />
            </div>
            <div className="form-row">
              <label>Tasa ({posTipo === 'compra' ? 'compra' : 'venta'})</label>
              <input
                type="number"
                min="0"
                step="0.0001"
                value={form.tasa}
                onChange={(e) => handleFormChange('tasa', e.target.value)}
                required
              />
            </div>
            <div className="total-preview">
              <span>Total en pesos:</span>
              <strong>{currencyFormatter(totalPesos)}</strong>
            </div>
            <div className="form-row grid">
              <div>
                <label>Cliente (opcional)</label>
                <input
                  type="text"
                  value={form.cliente}
                  onChange={(e) => handleFormChange('cliente', e.target.value)}
                  placeholder="Nombre del cliente"
                />
              </div>
              <div>
                <label>No. Autorización</label>
                <input
                  type="text"
                  value={form.autorizacion}
                  onChange={(e) => handleFormChange('autorizacion', e.target.value)}
                  placeholder="Referencia"
                />
              </div>
            </div>
            <div className="form-row">
              <label>Notas</label>
              <textarea
                value={form.notas}
                onChange={(e) => handleFormChange('notas', e.target.value)}
                placeholder="Observaciones"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={enviando}>
              {enviando ? 'Registrando...' : 'Confirmar Operación'}
            </button>
          </form>
        </section>

        <section className="tasas-card">
          <header>
            <h2>Tasas por moneda</h2>
            <p>Actualiza las tasas cuando sea necesario (solo administradores).</p>
          </header>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Moneda</th>
                  <th>Tasa compra</th>
                  <th>Tasa venta</th>
                  <th>Activo</th>
                  {esAdminExchange && <th></th>}
                </tr>
              </thead>
              <tbody>
                {monedas.map((moneda) => (
                  <tr key={moneda.id}>
                    <td>
                      <strong>{moneda.codigo}</strong>
                      <div className="subtext">{moneda.nombre}</div>
                    </td>
                    <td>
                      {esAdminExchange ? (
                        <input
                          type="number"
                          step="0.0001"
                          value={tasasEditadas[moneda.codigo]?.compra || ''}
                          onChange={(e) => handleCambioTasa(moneda.codigo, 'compra', e.target.value)}
                        />
                      ) : (
                        numberFormatter.format(moneda.tasa_compra || 0)
                      )}
                    </td>
                    <td>
                      {esAdminExchange ? (
                        <input
                          type="number"
                          step="0.0001"
                          value={tasasEditadas[moneda.codigo]?.venta || ''}
                          onChange={(e) => handleCambioTasa(moneda.codigo, 'venta', e.target.value)}
                        />
                      ) : (
                        numberFormatter.format(moneda.tasa_venta || 0)
                      )}
                    </td>
                    <td>
                      {esAdminExchange ? (
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={!!tasasEditadas[moneda.codigo]?.activo}
                            onChange={(e) => handleCambioTasa(moneda.codigo, 'activo', e.target.checked)}
                          />
                          <span></span>
                        </label>
                      ) : (
                        <span className={moneda.activo ? 'badge badge-success' : 'badge badge-danger'}>
                          {moneda.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      )}
                    </td>
                    {esAdminExchange && (
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleActualizarTasa(moneda.codigo)}
                          disabled={actualizandoTasa === moneda.codigo}
                        >
                          {actualizandoTasa === moneda.codigo ? 'Guardando...' : 'Guardar'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="inventario-card">
        <header>
          <h2>Inventario de divisas</h2>
          <p>Disponibilidad actual y valorización instantánea.</p>
        </header>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Moneda</th>
                <th>Disponible</th>
                <th>Costo promedio</th>
                <th>Valor compra</th>
                <th>Valor venta</th>
                <th>Actualizado</th>
              </tr>
            </thead>
            <tbody>
              {inventario.map((item) => (
                <tr key={item.codigo}>
                  <td>
                    <strong>{item.codigo}</strong>
                    <div className="subtext">{item.nombre}</div>
                  </td>
                  <td>{numberFormatter.format(item.cantidad_actual || 0)}</td>
                  <td>{currencyFormatter(item.costo_promedio || 0)}</td>
                  <td>{currencyFormatter(item.valor_compra || 0)}</td>
                  <td>{currencyFormatter(item.valor_venta || 0)}</td>
                  <td>{item.actualizado_en ? new Date(item.actualizado_en).toLocaleString() : '-'}</td>
                </tr>
              ))}
              {inventario.length === 0 && (
                <tr>
                  <td colSpan="6" className="empty-cell">
                    No hay inventario registrado aún.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="operaciones-card">
        <header>
          <h2>Últimas operaciones</h2>
          <p>Filtra por moneda, tipo y cantidad de registros para un análisis más rápido.</p>
        </header>
        <div className="filters-row inline">
          <select
            value={operacionesFiltro.moneda}
            onChange={(e) => setOperacionesFiltro((prev) => ({ ...prev, moneda: e.target.value }))}
          >
            <option value="">Todas las monedas</option>
            {monedas.map((moneda) => (
              <option key={moneda.id} value={moneda.codigo}>
                {moneda.codigo}
              </option>
            ))}
          </select>
          <select
            value={operacionesFiltro.tipo}
            onChange={(e) => setOperacionesFiltro((prev) => ({ ...prev, tipo: e.target.value }))}
          >
            <option value="">Todos los tipos</option>
            <option value="compra">Compra</option>
            <option value="venta">Venta</option>
          </select>
          <select
            value={operacionesFiltro.limite}
            onChange={(e) => setOperacionesFiltro((prev) => ({ ...prev, limite: Number(e.target.value) }))}
          >
            {[10, 20, 30, 50].map((limite) => (
              <option key={limite} value={limite}>
                {limite} registros
              </option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" onClick={() => cargarOperaciones()}>
            Aplicar
          </button>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Tipo</th>
                <th>Moneda</th>
                <th>Cantidad</th>
                <th>Tasa</th>
                <th>Total Pesos</th>
                <th>Ganancia</th>
                <th>Cliente</th>
                <th>Fecha</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {operaciones.map((op) => (
                <tr key={op.id}>
                  <td>{op.id}</td>
                  <td>
                    <span className={`badge ${op.tipo === 'venta' ? 'badge-success' : 'badge-info'}`}>
                      {op.tipo?.toUpperCase()}
                    </span>
                  </td>
                  <td>{op.moneda}</td>
                  <td>{numberFormatter.format(op.cantidad_divisa || 0)}</td>
                  <td>{numberFormatter.format(op.tasa || 0)}</td>
                  <td>{currencyFormatter(op.monto_pesos || 0)}</td>
                  <td>{currencyFormatter(op.ganancia || 0)}</td>
                  <td>{op.cliente || '-'}</td>
                  <td>{op.created_at ? new Date(op.created_at).toLocaleString() : '-'}</td>
                  <td className="action-cell">
                    <button className="btn btn-outline btn-sm" onClick={() => handleTicket(op.id)}>
                      PDF
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => enviarWhatsApp(op.id)}>
                      WhatsApp
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => enviarCorreo(op.id)}>
                      Correo
                    </button>
                  </td>
                </tr>
              ))}
              {operaciones.length === 0 && (
                <tr>
                  <td colSpan="9" className="empty-cell">
                    Aún no se registran operaciones.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="timeline-card">
        <header>
          <h2>Actividad rápida</h2>
          <p>Los últimos movimientos ordenados cronológicamente.</p>
        </header>
        <ul className="timeline-list">
          {operacionesHistorico.map((op) => (
            <li key={`timeline-${op.id}`}>
              <div className="timeline-dot" />
              <div>
                <span className="timeline-title">
                  #{op.id} · {op.tipo?.toUpperCase()} {op.moneda}
                </span>
                <p>
                  {numberFormatter.format(op.cantidad_divisa || 0)} al {numberFormatter.format(op.tasa || 0)} ·{' '}
                  {currencyFormatter(op.monto_pesos || 0)}
                </p>
                <small>{op.created_at ? new Date(op.created_at).toLocaleString() : '-'}</small>
              </div>
            </li>
          ))}
          {!operacionesHistorico.length && (
            <li className="empty-cell">Sin operaciones registradas todavía.</li>
          )}
        </ul>
      </section>

      <div className="reportes-grid">
        <section className="report-card">
          <header>
            <div>
              <h2>Flujo diario</h2>
              <p>Ventas y compras por moneda en la fecha seleccionada.</p>
            </div>
            <div className="filters-row">
              <input
                type="date"
                value={flujoFecha}
                onChange={(e) => {
                  setFlujoFecha(e.target.value);
                  cargarReporteFlujo(e.target.value);
                }}
              />
              <div className="export-buttons">
                <button className="btn btn-secondary btn-sm" onClick={() => exportarFlujo('excel')}>
                  Excel
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => exportarFlujo('pdf')}>
                  PDF
                </button>
              </div>
            </div>
          </header>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Moneda</th>
                  <th>Compras</th>
                  <th>Ventas</th>
                  <th>Ganancia</th>
                </tr>
              </thead>
              <tbody>
                {flujoDiario?.monedas?.map((item) => (
                  <tr key={item.codigo}>
                    <td>
                      <strong>{item.codigo}</strong>
                      <div className="subtext">{item.nombre}</div>
                    </td>
                    <td>{currencyFormatter(item.total_compras || 0)}</td>
                    <td>{currencyFormatter(item.total_ventas || 0)}</td>
                    <td>{currencyFormatter(item.ganancia || 0)}</td>
                  </tr>
                ))}
                {!flujoDiario?.monedas?.length && (
                  <tr>
                    <td colSpan="4" className="empty-cell">
                      No hay operaciones registradas para esta fecha.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {flujoDiario?.totales && (
            <div className="totales-resumen">
              <div>
                <span>Total Compras</span>
                <strong>{currencyFormatter(flujoDiario.totales.compras || 0)}</strong>
              </div>
              <div>
                <span>Total Ventas</span>
                <strong>{currencyFormatter(flujoDiario.totales.ventas || 0)}</strong>
              </div>
              <div>
                <span>Ganancia</span>
                <strong>{currencyFormatter(flujoDiario.totales.ganancia || 0)}</strong>
              </div>
            </div>
          )}
        </section>

        <section className="report-card">
          <header>
            <div>
              <h2>Ganancias por rango</h2>
              <p>Histórico de ganancias agrupado por fecha y moneda.</p>
            </div>
            <div className="filters-row range">
              <input
                type="date"
                value={gananciasFiltro.desde}
                onChange={(e) =>
                  setGananciasFiltro((prev) => ({ ...prev, desde: e.target.value }))
                }
              />
              <input
                type="date"
                value={gananciasFiltro.hasta}
                onChange={(e) =>
                  setGananciasFiltro((prev) => ({ ...prev, hasta: e.target.value }))
                }
              />
              <select
                value={gananciasFiltro.moneda}
                onChange={(e) =>
                  setGananciasFiltro((prev) => ({ ...prev, moneda: e.target.value }))
                }
              >
                <option value="">Todas las monedas</option>
                {monedas.map((moneda) => (
                  <option key={moneda.id} value={moneda.codigo}>
                    {moneda.codigo}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => cargarReporteGanancias(gananciasFiltro)}
                disabled={cargandoReporte}
              >
                {cargandoReporte ? 'Cargando...' : 'Aplicar'}
              </button>
              <div className="export-buttons">
                <button className="btn btn-secondary btn-sm" onClick={() => exportarGanancias('excel')}>
                  Excel
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => exportarGanancias('pdf')}>
                  PDF
                </button>
              </div>
            </div>
          </header>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Moneda</th>
                  <th>Operaciones</th>
                  <th>Monto total</th>
                  <th>Ganancia</th>
                </tr>
              </thead>
              <tbody>
                {gananciasDatos?.registros?.map((row, idx) => (
                  <tr key={`${row.fecha}-${row.codigo}-${idx}`}>
                    <td>{row.fecha}</td>
                    <td>{row.codigo}</td>
                    <td>{row.operaciones}</td>
                    <td>{currencyFormatter(row.monto_total || 0)}</td>
                    <td>{currencyFormatter(row.ganancia || 0)}</td>
                  </tr>
                ))}
                {!gananciasDatos?.registros?.length && (
                  <tr>
                    <td colSpan="5" className="empty-cell">
                      No hay registros en el rango seleccionado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {gananciasDatos?.totales && (
            <div className="totales-resumen">
              <div>
                <span>Operaciones</span>
                <strong>{gananciasDatos.totales.operaciones || 0}</strong>
              </div>
              <div>
                <span>Monto total</span>
                <strong>{currencyFormatter(gananciasDatos.totales.monto_total || 0)}</strong>
              </div>
              <div>
                <span>Ganancia total</span>
                <strong>{currencyFormatter(gananciasDatos.totales.ganancia || 0)}</strong>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Exchange;

